import { contextSchemas } from '../context/analytics-contexts.types';
import { useAnalyticsTracking } from '../context/AnalyticsTrackingContext';
import { emitUIEvent } from '../ui-event';
/**
 * `useEmitUiEvent` is a hook for emitting Snowplow UI events with analytics context.
 *
 * It pulls the current context from `AnalyticsTrackingProvider` and
 * merges it with any `extraContext` you pass in. The merged result is then used
 * to attach relevant entities (e.g. consultation, feature flags) to the emitted event.
 *
 * Only keys defined in `contextSchemas` will be included in the emitted context.
 * Any unknown keys in `extraContext` will be ignored silently.
 *
 * Note: The `uiEvent` key in the context is NOT merged into Snowplow contexts.
 * It's only used for components to access the `context_id` via `context_id`.
 * This prevents duplicate `ui_event` contexts in Snowplow events.
 *
 * This is useful when you want to enrich a single event with context from the provider
 *
 * Example:
 * ```tsx
 * const emitEvent = useEmitUiEvent();
 * emitEvent(
 *   {
 *     component_name: 'ConsentRequestButton',
 *     event_name: 'send_request_consent',
 *     id: 'send_request_btn',
 *     value: 'Send Request'
 *   },
 *   {
 *     consultation: {
 *       ...ctx.consultation,
 *       consultation_id: '123'
 *     }
 *   }
 * );
 * ```
 */
export const useEmitUiEvent = () => {
    const context = useAnalyticsTracking();
    return (event, extraContext) => {
        const merged = {
            ...context,
            ...extraContext,
        };
        const contexts = Object.entries(merged).flatMap(([key, value]) => value && key !== 'uiEvent'
            ? [{ schema: contextSchemas[key], data: value }]
            : []);
        emitUIEvent(event, contexts);
    };
};
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoidXNlRW1pdFVJRXZlbnQuanMiLCJzb3VyY2VSb290IjoiIiwic291cmNlcyI6WyIuLi8uLi8uLi9zcmMvbGliL2hvb2tzL3VzZUVtaXRVSUV2ZW50LnRzIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiJBQUVBLE9BQU8sRUFBRSxjQUFjLEVBQUUsTUFBTSxxQ0FBcUMsQ0FBQztBQUNyRSxPQUFPLEVBQUUsb0JBQW9CLEVBQUUsTUFBTSxxQ0FBcUMsQ0FBQztBQUMzRSxPQUFPLEVBQUUsV0FBVyxFQUFFLE1BQU0sYUFBYSxDQUFDO0FBRTFDOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7O0dBa0NHO0FBRUgsTUFBTSxDQUFDLE1BQU0sY0FBYyxHQUFHLEdBQUcsRUFBRTtJQUNqQyxNQUFNLE9BQU8sR0FBRyxvQkFBb0IsRUFBRSxDQUFDO0lBRXZDLE9BQU8sQ0FBQyxLQUFzQyxFQUFFLFlBQXFDLEVBQUUsRUFBRTtRQUN2RixNQUFNLE1BQU0sR0FBRztZQUNiLEdBQUcsT0FBTztZQUNWLEdBQUcsWUFBWTtTQUNoQixDQUFDO1FBRUYsTUFBTSxRQUFRLEdBQUcsTUFBTSxDQUFDLE9BQU8sQ0FBQyxNQUFNLENBQUMsQ0FBQyxPQUFPLENBQUMsQ0FBQyxDQUFDLEdBQUcsRUFBRSxLQUFLLENBQUMsRUFBRSxFQUFFLENBQy9ELEtBQUssSUFBSSxHQUFHLEtBQUssU0FBUztZQUN4QixDQUFDLENBQUMsQ0FBQyxFQUFFLE1BQU0sRUFBRSxjQUFjLENBQUMsR0FBa0MsQ0FBQyxFQUFFLElBQUksRUFBRSxLQUFlLEVBQUUsQ0FBQztZQUN6RixDQUFDLENBQUMsRUFBRSxDQUNQLENBQUM7UUFFRixXQUFXLENBQUMsS0FBSyxFQUFFLFFBQVEsQ0FBQyxDQUFDO0lBQy9CLENBQUMsQ0FBQztBQUNKLENBQUMsQ0FBQyJ9